import React, { useState, useEffect } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { useParams,useNavigate } from "react-router-dom";
import Select from "react-select";
import { DndContext, useDroppable, useDraggable } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import moment from 'moment';

export const AdminAthleteCreateDynamicStrengthWorkout = () => {
  const navigate = useNavigate();
  const { staff } = useStaffAuthContext();
  const { athlete_id } = useParams();

  const [athleteName, setAthleteName] = useState("");
  const [error, setError] = useState("");

  // State variables for Start Date, End Date, and Day
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [day, setDay] = useState("Monday");
  
  const [exerciseId, setExerciseId] = useState("");
  const [sets, setSets] = useState([{ reps: 0, weight: 0, weight_unit: "%", goal: '< 0.5 m/s (Absolute Strength)' }]);
  const [restDetails, setRestDetails] = useState("");
  const [notes, setNotes] = useState("");

  const [submittedData, setSubmittedData] = useState(null); // State to store submitted data
  const [selectedExercises, setSelectedExercises] = useState([]); // State to store selected exercises for Superset

  const [exerciseOptions, setExerciseOptions] = useState([]);

  // Tab state
  const [activeTab, setActiveTab] = useState("exercise");

  const [strengthData, setStrengthData] = useState({});
  const [readerror, setReadError] = useState("");


  const [workoutData, setWorkoutData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(null);

  const [dragMode, setDragMode] = useState(false);  // State to control drag mode

    // State to trigger refetch after a workout is saved
    const [isWorkoutSaved, setIsWorkoutSaved] = useState(false);

    const fetchAthlete = async () => {
      try {
        const response = await fetch(`${API_URL.GET_ATHLETE_BY_ID}${athlete_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${staff.token}`,
          },
        });

        const data = await response.json();

        if (!response.ok) {
          setError(data.error || "Failed to fetch athlete details");
          return;
        }

        setAthleteName(data.firstname + " " + data.lastname); // Adjust based on actual response structure
      } catch (err) {
        setError("An error occurred while fetching the atihlete details.");
      }
    };

    const fetchExercises = async () => {
      try {
        const response = await fetch(`${API_URL.SELECT_STRENGTH_EXERCISE_ALL}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${staff.token}`,
          },
        });
        const data = await response.json();
       // console.log(data);

        if (!response.ok) {
          setError(data.error || "Failed to fetch exercises");
          return;
        }

        const exercises = data.exercises.map(exercise => ({
          value: exercise._id,  // Adjust this field based on your response structure
          label: exercise.title,  // Adjust this field based on your response structure
        }));

        setExerciseOptions(exercises);

      } catch (err) {
        setError("An error occurred while fetching exercises.");
      }
    };

    // Fetch data from backend service
    const fetchStrengthData = async () => {
      try {
        const response = await fetch(`${API_URL.GET_DYNAMIC_STRENGTH_WORKOUT_BY_ATHLETE}${athlete_id}`,{
          method: "GET",
          "Content-Type": "application/json",
          headers: {
            Authorization: `Bearer ${staff.token}`,
          },
        });
        const data = await response.json();
        console.log(data);
  
        if (!response.ok) {
          setReadError(data.error || 'Failed to fetch data');
          return;
        }
        if (data.workoutData) {
          // Safeguard the data by ensuring each date entry has a combined array
          const updatedData = {
            athlete_id: data.workoutData.athlete_id,
            dates: data.workoutData.dates.map((dateEntry) => ({
              ...dateEntry,
              combined: dateEntry.combined || [], // Initialize as empty array if missing
            })),
          };
    
          setWorkoutData(updatedData);
        } else {
          setWorkoutData([]); // Clear the data if no workoutData is found
        }
        setLoading(false);
  
       //console.log(workoutData);
  
      } catch (err) {
        setReadError('An error occurred while fetching workout data.');
      }
    };

  // Fetch athlete details
  useEffect(() => {
    fetchAthlete();
    fetchExercises(); // Fetch exercises when component mounts
    fetchStrengthData();
  }, [athlete_id, staff.token]);

      // Trigger refetch after saving a workout
useEffect(() => {
  if (isWorkoutSaved) {
    fetchStrengthData();
    setIsWorkoutSaved(false); // Reset after fetching
  }
}, [isWorkoutSaved]); // Refetch after saving


  const handleSetChange = (index, field, value) => {
    const newSets = [...sets];
    newSets[index][field] = value;
    setSets(newSets);
  };

  const addSet = () => {
    if (sets.length > 0) {
      const lastSet = sets[sets.length - 1]; // Get the last set
      setSets([...sets, { ...lastSet }]); // Spread the last set's data into a new object
    } else {
      setSets([{ reps: 0, weight: 0, weight_unit: "%", goal: '< 0.5 m/s (Absolute Strength)' }]); // Default set if no sets exist
    }
  };

  const removeSet = (index) => {
    setSets(sets.filter((_, i) => i !== index));
  };

  const handleCreateExercise = async (e) => {
    e.preventDefault();
    
    // Prepare the exercise data
    const exerciseData = {
      athlete_id,  // From URL params
      startDate,   // Start date selected by the user
      endDate,     // End date selected by the user
      day,         // Selected day (e.g., "Monday")
      exercises: [{
        exercise_id: exerciseId.value,  // Exercise ID from dropdown
        sets: sets.map(set => ({
          reps: set.reps,               // Reps for the set
          weight: set.weight,           // Weight for the set
          weight_unit: set.weight_unit, // Weight unit (e.g., kg, lbs)
          goal: set.goal                // Goal for the set (e.g., strength)
        })),
        rest_details: restDetails,      // Rest details from input field
        notes                           // Notes from input field
      }]
    };
  
    // Submit the exercise data to the backend
    submitForm(API_URL.POST_ATHLETE_DYNAMIC_STRENGTH_WORKOUT_EXERCISE + athlete_id, exerciseData);
  };


  const handleCreateSuperSet = async (e) => {
    e.preventDefault();

     // Prepare the exercise data
     const supersets = {
      athlete_id,  // From URL params
      startDate,   // Start date selected by the user
      endDate,     // End date selected by the user
      day,         // Selected day (e.g., "Monday")
                   // Selected day (e.g., "Monday")
      supersets: [{
          exercise_ids: selectedExercises.map(exercise => exercise.value),   // Exercise IDs selected from the dropdown
          sets: sets.map(set => ({
            reps: set.reps,                // Reps value for the set
            weight: set.weight,            // Weight value for the set
            weight_unit: set.weight_unit,  // Weight unit (kg, lbs, etc.)
            goal: set.goal                 // Goal for the set (strength, hypertrophy, etc.)
          })),
          rest_details:restDetails,           // Rest details (from input field)
          notes     
        },  
      ],

                // Notes (from input field)
    };


    //setSubmittedData(supersets); // Store the submitted data in state
    submitForm(API_URL.POST_ATHLETE_DYNAMIC_STRENGTH_WORKOUT_SUPERSET+athlete_id,supersets);

  };


  const handleCreateCircuit = async (e) => {
    e.preventDefault();

     // Prepare the exercise data
     const circuits = {
    athlete_id,  // From URL params
    startDate,   // Start date selected by the user
    endDate,     // End date selected by the user
    day,          // From URL para          // Selected week (e.g., "Week 1")
                   // Selected day (e.g., "Monday")
    circuits: [{
          exercise_ids: selectedExercises.map(exercise => exercise.value),   // Exercise IDs selected from the dropdown
          sets: sets.map(set => ({
            reps: set.reps,                // Reps value for the set
            weight: set.weight,            // Weight value for the set
            weight_unit: set.weight_unit,  // Weight unit (kg, lbs, etc.)
            goal: set.goal                 // Goal for the set (strength, hypertrophy, etc.)
          })),
          rest_details:restDetails,           // Rest details (from input field)
          notes     
        },  
      ],
  
                // Notes (from input field)
    };


    //setSubmittedData(circuits); // Store the submitted data in state
    submitForm(API_URL.POST_ATHLETE_DYNAMIC_STRENGTH_WORKOUT_CIRCUIT+athlete_id,circuits);

  };

  const submitForm = async (url, body) => {
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      if (!response.ok) {
        alert(data.error || "Failed to create item.");
      } else {
        alert("Exercise Added successfully!");
        resetFormState();
        setIsWorkoutSaved(true); // Mark that the workout was saved
      }
    } catch (err) {
      alert("An error occurred while creating the exercise.");
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    resetFormState(); // Reset the form state when the tab changes
  };

  const resetFormState = () => {
    // Reset the state to initial values
    setExerciseId("");
    setSelectedExercises([]);
    setSets([{ reps: 0, weight: 0, weight_unit: "%", goal: '< 0.5 m/s (Absolute Strength)' }]);
    setRestDetails("");
    setNotes("");
  };


  const handleDragEnd = (event, dateIndex) => {
    const { active, over } = event;
    const currentDateEntry = workoutData?.dates?.[dateIndex];
  
    // If current data is invalid, return early
    if (!currentDateEntry || !currentDateEntry.combined) {
      console.warn(`Invalid data at dateIndex ${dateIndex}.`);
      return;
    }
  
    // Ensure that the active item is an index of the combined array
    const activeIndex = currentDateEntry.combined.findIndex(
      (item) => item._id === active.id
    );
    const overIndex = currentDateEntry.combined.findIndex(
      (item) => item._id === over.id
    );
  
    if (activeIndex === -1 || overIndex === -1) {
      console.warn('Invalid drag target or source.');
      return;
    }
  
    // Safeguard that both active and over items exist
    const activeItem = currentDateEntry.combined[activeIndex];
    const overItem = currentDateEntry.combined[overIndex];
  
    // If the drag action is valid, move the item in the array
    const updatedCombined = [...currentDateEntry.combined];
    updatedCombined.splice(activeIndex, 1); // Remove the active item
    updatedCombined.splice(overIndex, 0, activeItem); // Insert the active item at the new position
  
    // Update the workout data
    const updatedData = {
      ...workoutData,
      dates: workoutData.dates.map((date, index) =>
        index === dateIndex ? { ...date, combined: updatedCombined } : date
      ),
    };
  
    setWorkoutData(updatedData);
    console.log('Updated workout data after drag:', updatedData);

     // Prepare the new order
  const newOrder = updatedCombined.map((item, index) => ({
    _id: item._id,
    order_id: index + 1, // Setting the order_id based on the new index
    type: item.type, // Include the item type (e.g., exercise, superset, circuit)
  }));

  // Call updateOrderInDatabase to save the new order in the database
    updateOrderInDatabase(newOrder, dateIndex);
  
  };


  const updateOrderInDatabase = async (newOrder, dateIndex) => {
    console.log("Sending order update to server...");
    console.log(newOrder);
  
    try {
      const response = await fetch(API_URL.POST_ATHLETE_DYNAMIC_STRENGTH_WORKOUT_DATE_ORDER + athlete_id, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${staff.token}`,
        },
        body: JSON.stringify({ combined: newOrder, dateIndex }),
      });
      
      const data = await response.json();
      if (!response.ok) {
        console.error("Error response from server:", data);
        setError('Failed to update order in database: ' + data.message);
        return;
      }
      
      setSuccess('Order updated successfully.');
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error("Error caught while updating order:", error);
      setError('An error occurred while updating the order: ' + error.message);
    }
  };

const handleDelete = async (id,type) => {
  try {
    const response = await fetch(API_URL.DELETE_ATHLETE_DYNAMIC_STRENGTH_DATE_BLOCK_BY_ID + id + "/" + type, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${staff.token}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Error deleting record:', errorData.message);
      alert('Failed to delete the record.');
    } else {
      console.log(`Record with id ${id} and type ${type} deleted successfully.`);
      alert('Record deleted successfully.');
      // Optionally refresh the list or call a function to update state
       // Replace with your refresh logic
       setIsWorkoutSaved(true); // Mark that the workout was saved
    }
  } catch (error) {
    console.error('Error:', error.message);
    alert('An error occurred while deleting the record.');
  }
};

 // Toggle drag mode
 const toggleDragMode = () => {
  setDragMode((prev) => !prev);
};

    
  

  return (
    <div className="flex h-screen">
    
        <StaffSideMenu />
        <div className="flex-1 flex-grow p-6 flex flex-row">
      
        {/* Left Section: Manage Existing Static Workout */}
        <div className="w-1/2 p-4 border-r-2 h-[100vh] overflow-y-auto">
     
      {readerror && <p className="text-red-500">{readerror}</p>}
     
      {loading && (
        <div className="mb-4 p-4 text-blue-700 bg-blue-100 rounded">
          Loading Workout...
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 text-red-700 bg-red-100 rounded">
          Error: {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 text-green-700 bg-green-100 rounded">
          {success}
        </div>
      )}

        {workoutData?.dates?.length > 0 ? (
        workoutData.dates.map((dateEntry, dateIndex) => (
            <div key={dateIndex} className="mb-8 p-4 border rounded-lg bg-gray-50 shadow">
        
            <div className="ml-6 mb-6">

              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-700">{moment(dateEntry.date).format('MMMM Do YYYY')}</h3>
                <button
                  onClick={toggleDragMode}
                  className="px-4 py-2 bg-blue-600 text-white font-medium rounded shadow-sm hover:bg-blue-500 transition duration-150"
                >
                  {dragMode ? 'Disable Drag Mode' : 'Enable Drag Mode'}
                </button>
              </div>
              {dragMode && (
                <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 rounded mb-4">
                  By enabling the drag mode, you can arrange the order of the different exercises by dragging and dropping them. Once done, please disable the mode again to finalize your changes.
                </div>
              )}

                <DndContext onDragEnd={(event) => handleDragEnd(event, dateIndex)}>
                <div className="space-y-4">
                    {/* Check if combined exists before rendering */}
                    {dateEntry.combined && dateEntry.combined.length > 0 ? (
                    dateEntry.combined.map((item, itemIndex) => (
                        <Droppable key={item._id || itemIndex} id={item._id || `${itemIndex}`}>
                        <DraggableItem
                            key={item._id || itemIndex}
                            id={item._id || `${itemIndex}`}
                            index={itemIndex}
                            item={item}
                            athlete_id={athlete_id}
                            dragMode={dragMode} // Pass dragMode state as prop
                            onDelete={handleDelete}
                        />
                        </Droppable>
                    ))
                    ) : (
                    <p>No items for this day.</p> // Optional: Show a message if there are no items
                    )}
                </div>
                </DndContext>
            </div>
            </div>
        ))
        ) : (
        <p>No workout data available.</p>
        )}
         
      </div>


      <div className="w-1/2 p-4">
      <button
            onClick={() => navigate(-1)}
            className="absolute top-4 right-4 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Go Back
          </button>
        <h1 className="text-2xl font-bold mb-4">Build Dynamic Strength Workout</h1>
        {error && <p className="text-red-500">{error}</p>}
        {!error && athleteName && (
          <h2 className="text-xl font-semibold mb-4">Athete: <span className="text-green-600">{athleteName}</span></h2>
        )}

        <div className="flex space-x-4">
          <div className="mb-4 flex-1">
            <label htmlFor="start-date" className="block text-sm font-medium text-gray-700">Start Date</label>
            <input
              type="date"
              id="start-date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div className="mb-4 flex-1">
            <label htmlFor="end-date" className="block text-sm font-medium text-gray-700">End Date</label>
            <input
              type="date"
              id="end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full border rounded px-3 py-2"
            />
          </div>

          <div className="mb-4 flex-1">
            <label htmlFor="day" className="block text-sm font-medium text-gray-700">Day</label>
            <select
              id="day"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              className="w-full border rounded px-3 py-2"
            >
              {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((dayOption) => (
                <option key={dayOption} value={dayOption}>{dayOption}</option>
              ))}
            </select>
          </div>
        </div>


        {/* Tabs */}
        <div className="mb-4 flex space-x-4">
          <button
            onClick={() => handleTabClick("exercise")}
            className={`px-4 py-2 text-sm font-semibold ${activeTab === "exercise" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
          >
            Add Exercise
          </button>
          <button
            onClick={() => handleTabClick("superset")}
            className={`px-4 py-2 text-sm font-semibold ${activeTab === "superset" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
          >
            Add SuperSet
          </button>
          <button
            onClick={() => handleTabClick("circuit")}
            className={`px-4 py-2 text-sm font-semibold ${activeTab === "circuit" ? "bg-blue-500 text-white" : "bg-gray-200"}`}
          >
            Add Circuit
          </button>
        </div>

        {/* Tab Content */}
        {activeTab === "exercise" && (
          <section className="mb-8 flex-grow">
            <h2 className="text-xl font-semibold mb-4">Add Exercise</h2>
            <form onSubmit={handleCreateExercise} className="space-y-6 bg-white p-6 shadow-md rounded">
              <div className="mb-4">
                <label htmlFor="exerciseId" className="block text-sm font-medium text-gray-700">Select Exercise</label>
                <Select
                id="exerciseId"
                options={exerciseOptions}
                value={exerciseId}
                onChange={setExerciseId}  // Update selected exercise
                placeholder="Select Exercise"
                getOptionLabel={(e) => e.label}
                getOptionValue={(e) => e.value}
                required
                className="w-full"
              />
              </div>
              <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">Add Set Details</label>
                  {sets.map((set, index) => (
                    <div key={index} className="grid grid-cols-5 gap-2 items-center mb-2">
                      <div>
                        <label htmlFor={`reps-${index}`} className="text-sm">Reps</label>
                        <input
                          id={`reps-${index}`}
                          type="number"
                          placeholder="Reps"
                          value={set.reps}
                          onChange={(e) => handleSetChange(index, "reps", e.target.value)}
                          required
                          className="w-full border rounded px-2 py-1"
                        />
                      </div>
                      <div>
                        <label htmlFor={`weight-${index}`} className="text-sm">Weight</label>
                        <input
                          id={`weight-${index}`}
                          type="number"
                          placeholder="Weight"
                          value={set.weight}
                          onChange={(e) => handleSetChange(index, "weight", e.target.value)}
                          required
                          className="w-full border rounded px-2 py-1"
                        />
                      </div>
                      <div>
                        <label htmlFor={`unit-${index}`} className="text-sm">Unit</label>
                        <select
                          id={`unit-${index}`}
                          value={set.weight_unit}
                          onChange={(e) => handleSetChange(index, "weight_unit", e.target.value)}
                          className="w-full border rounded px-2 py-1"
                        >
                          <option value="%">%</option>
                          <option value="lbs">lbs</option>
                          <option value="Hide">Hide</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor={`goal-${index}`} className="text-sm">Goal</label>
                        <select
                          id={`goal-${index}`}
                          value={set.goal}
                          onChange={(e) => handleSetChange(index, "goal", e.target.value)}
                          className="w-full border rounded px-2 py-1 min-w-[90px] md:min-w-[90px]"
                        >
                          <option value="< 0.5 m/s (Absolute Strength)">&lt; 0.5 m/s (Absolute Strength)</option>
                          <option value="0.5 - 0.75 m/s (Accelerative Strength)">0.5 - 0.75 m/s (Accelerative Strength)</option>
                          <option value="0.75 - 1 m/s (Strength Speed)">0.75 - 1 m/s (Strength Speed)</option>
                          <option value="1 - 1.3 m/s (Speed Strength)">1 - 1.3 m/s (Speed Strength)</option>
                          <option value="> 1.3 m/s (Starting Strength)">&gt; 1.3 m/s (Starting Strength)</option>
                          <option value="> 2 m/s (Speed)">&gt; 2 m/s (Speed)</option>
                        </select>
                      </div>
                      <div className="flex items-center h-full">
                        <button
                          type="button"
                          onClick={() => removeSet(index)}
                          className="text-red-500 font-bold"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                <div className="flex justify-end">
                      <button type="button" onClick={addSet} className="text-blue-500 font-bold mt-2">
                        Add Set
                      </button>
                </div>     
             </div>


                <div className="mb-4">
                  <label htmlFor="restDetails" className="block text-sm font-medium text-gray-700">Rest Details</label>
                  <textarea
                    id="restDetails"
                    placeholder="Enter Rest Details"
                    value={restDetails}
                    onChange={(e) => setRestDetails(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    rows="3"
                  ></textarea>
                </div>
                <div className="mb-4">
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    id="notes"
                    placeholder="Enter Notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    rows="4"
                  ></textarea>
                </div>
                <div className="flex justify-center">
                  <button type="submit" className="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded">
                    Add Exercise
                  </button>
                </div>        
            </form>
          </section>
        )}

        {/* SuperSet Tab */}
        {activeTab === "superset" && (
          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Add SuperSet</h2>
            <form onSubmit={handleCreateSuperSet} className="space-y-6 bg-white p-6 shadow-md rounded">
              <div className="mb-4">
                <label htmlFor="exercises" className="block text-sm font-medium text-gray-700">Select Exercises</label>
               
                <Select
                  id="exerciseId"
                  options={exerciseOptions}
                  value={selectedExercises} // Array of selected options
                  onChange={setSelectedExercises} // `setSelectedExercises` should handle the array directly
                  placeholder="Select Exercises"
                  isMulti // Enable multi-select
                  getOptionLabel={(e) => e.label}
                  getOptionValue={(e) => e.value}
                  required
                  className="w-full"
                />
        
              </div>
              <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">Add Set Details</label>
                  {sets.map((set, index) => (
                    <div key={index} className="grid grid-cols-5 gap-2 items-center mb-2">
                      <div>
                        <label htmlFor={`reps-${index}`} className="text-sm">Reps</label>
                        <input
                          id={`reps-${index}`}
                          type="number"
                          placeholder="Reps"
                          value={set.reps}
                          onChange={(e) => handleSetChange(index, "reps", e.target.value)}
                          required
                          className="w-full border rounded px-2 py-1"
                        />
                      </div>
                      <div>
                        <label htmlFor={`weight-${index}`} className="text-sm">Weight</label>
                        <input
                          id={`weight-${index}`}
                          type="number"
                          placeholder="Weight"
                          value={set.weight}
                          onChange={(e) => handleSetChange(index, "weight", e.target.value)}
                          required
                          className="w-full border rounded px-2 py-1"
                        />
                      </div>
                      <div>
                        <label htmlFor={`unit-${index}`} className="text-sm">Unit</label>
                        <select
                          id={`unit-${index}`}
                          value={set.weight_unit}
                          onChange={(e) => handleSetChange(index, "weight_unit", e.target.value)}
                          className="w-full border rounded px-2 py-1"
                        >
                          <option value="%">%</option>
                          <option value="lbs">lbs</option>
                          <option value="Hide">Hide</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor={`goal-${index}`} className="text-sm">Goal</label>
                        <select
                          id={`goal-${index}`}
                          value={set.goal}
                          onChange={(e) => handleSetChange(index, "goal", e.target.value)}
                          className="w-full border rounded px-2 py-1 min-w-[90px] md:min-w-[90px]"
                        >
                          <option value="< 0.5 m/s (Absolute Strength)">&lt; 0.5 m/s (Absolute Strength)</option>
                          <option value="0.5 - 0.75 m/s (Accelerative Strength)">0.5 - 0.75 m/s (Accelerative Strength)</option>
                          <option value="0.75 - 1 m/s (Strength Speed)">0.75 - 1 m/s (Strength Speed)</option>
                          <option value="1 - 1.3 m/s (Speed Strength)">1 - 1.3 m/s (Speed Strength)</option>
                          <option value="> 1.3 m/s (Starting Strength)">&gt; 1.3 m/s (Starting Strength)</option>
                          <option value="> 2 m/s (Speed)">&gt; 2 m/s (Speed)</option>
                        </select>
                      </div>
                      <div className="flex items-center h-full">
                        <button
                          type="button"
                          onClick={() => removeSet(index)}
                          className="text-red-500 font-bold"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                <div className="flex justify-end">
                      <button type="button" onClick={addSet} className="text-blue-500 font-bold mt-2">
                        Add Set
                      </button>
                </div>     
             </div>

             <div className="mb-4">
                  <label htmlFor="restDetails" className="block text-sm font-medium text-gray-700">Rest Details</label>
                  <textarea
                    id="restDetails"
                    placeholder="Enter Rest Details"
                    value={restDetails}
                    onChange={(e) => setRestDetails(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    rows="3"
                  ></textarea>
                </div>
                <div className="mb-4">
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    id="notes"
                    placeholder="Enter Notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    rows="4"
                  ></textarea>
                </div>
                <div className="flex justify-center">
                    <button type="submit" className="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded">Add SuperSet</button>
                </div>
            </form>
          </section>
        )}

          {/* Circuit Tab */}
          {activeTab === "circuit" && (
          <section className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Add Circuit</h2>
            <form onSubmit={handleCreateCircuit} className="space-y-6 bg-white p-6 shadow-md rounded">
              <div className="mb-4">
                <label htmlFor="exercises" className="block text-sm font-medium text-gray-700">Select Exercises</label>
                <Select
                  id="exerciseId"
                  options={exerciseOptions}
                  value={selectedExercises} // Array of selected options
                  onChange={setSelectedExercises} // `setSelectedExercises` should handle the array directly
                  placeholder="Select Exercises"
                  isMulti // Enable multi-select
                  getOptionLabel={(e) => e.label}
                  getOptionValue={(e) => e.value}
                  required
                  className="w-full"
                />
        
              </div>
              <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700">Add Set Details</label>
                  {sets.map((set, index) => (
                    <div key={index} className="grid grid-cols-5 gap-2 items-center mb-2">
                      <div>
                        <label htmlFor={`reps-${index}`} className="text-sm">Reps</label>
                        <input
                          id={`reps-${index}`}
                          type="number"
                          placeholder="Reps"
                          value={set.reps}
                          onChange={(e) => handleSetChange(index, "reps", e.target.value)}
                          required
                          className="w-full border rounded px-2 py-1"
                        />
                      </div>
                      <div>
                        <label htmlFor={`weight-${index}`} className="text-sm">Weight</label>
                        <input
                          id={`weight-${index}`}
                          type="number"
                          placeholder="Weight"
                          value={set.weight}
                          onChange={(e) => handleSetChange(index, "weight", e.target.value)}
                          required
                          className="w-full border rounded px-2 py-1"
                        />
                      </div>
                      <div>
                        <label htmlFor={`unit-${index}`} className="text-sm">Unit</label>
                        <select
                          id={`unit-${index}`}
                          value={set.weight_unit}
                          onChange={(e) => handleSetChange(index, "weight_unit", e.target.value)}
                          className="w-full border rounded px-2 py-1"
                        >
                          <option value="%">%</option>
                          <option value="lbs">lbs</option>
                          <option value="Hide">Hide</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor={`goal-${index}`} className="text-sm">Goal</label>
                        <select
                          id={`goal-${index}`}
                          value={set.goal}
                          onChange={(e) => handleSetChange(index, "goal", e.target.value)}
                          className="w-full border rounded px-2 py-1 min-w-[90px] md:min-w-[90px]"
                        >
                          <option value="< 0.5 m/s (Absolute Strength)">&lt; 0.5 m/s (Absolute Strength)</option>
                          <option value="0.5 - 0.75 m/s (Accelerative Strength)">0.5 - 0.75 m/s (Accelerative Strength)</option>
                          <option value="0.75 - 1 m/s (Strength Speed)">0.75 - 1 m/s (Strength Speed)</option>
                          <option value="1 - 1.3 m/s (Speed Strength)">1 - 1.3 m/s (Speed Strength)</option>
                          <option value="> 1.3 m/s (Starting Strength)">&gt; 1.3 m/s (Starting Strength)</option>
                          <option value="> 2 m/s (Speed)">&gt; 2 m/s (Speed)</option>
                        </select>
                      </div>
                      <div className="flex items-center h-full">
                        <button
                          type="button"
                          onClick={() => removeSet(index)}
                          className="text-red-500 font-bold"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                <div className="flex justify-end">
                      <button type="button" onClick={addSet} className="text-blue-500 font-bold mt-2">
                        Add Set
                      </button>
                </div>     
             </div>

             <div className="mb-4">
                  <label htmlFor="restDetails" className="block text-sm font-medium text-gray-700">Rest Details</label>
                  <textarea
                    id="restDetails"
                    placeholder="Enter Rest Details"
                    value={restDetails}
                    onChange={(e) => setRestDetails(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    rows="3"
                  ></textarea>
                </div>
                <div className="mb-4">
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    id="notes"
                    placeholder="Enter Notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    rows="4"
                  ></textarea>
                </div>
                <div className="flex justify-center">
                    <button type="submit" className="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded">Add Circuit</button>
                 </div> 
             </form>
          </section>
        )}

        {/* Render the submitted data */}
        {submittedData && (
          <section className="mt-8">
            <h3 className="text-xl font-semibold">Submitted Data</h3>
            <pre className="bg-gray-200 p-4 rounded">{JSON.stringify(submittedData, null, 2)}</pre>
          </section>
        )}
      </div>
    </div>


    </div>
  );
};

const DraggableItem = ({ athlete_id,id, item, dragMode, onDelete }) => {
  const [isDragging, setIsDragging] = useState(false);  // Track dragging state

  const navigate = useNavigate();
 
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id,
    enabled: dragMode // Only enable drag if dragMode is true and delete is not clicked
  });

  const style = transform
  ? {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      transition: isDragging ? 'none' : 'transform 0.2s ease-out',  // Smooth transition when not dragging
    }
  : undefined;

const handleDeleteClick = (event) => {
  event.stopPropagation(); // Prevent drag trigger
  const confirmation = window.confirm(`Are you sure you want to delete this ${item.type}?`);
  if (confirmation) {
    onDelete(item._id, item.type);
  }
};

const handleEditClick = (event) => {
  event.stopPropagation(); // Prevent drag trigger

  if(item.type==="exercise"){
    navigate('/administrator/dynamic-strength-workout/edit/exercise/' + item._id + "/" + athlete_id); 
  }
  else if(item.type==="superset"){
    navigate('/administrator/dynamic-strength-workout/edit/superset/' + item._id + "/" + athlete_id); 
  }
  else if(item.type==="circuit"){
    navigate('/administrator/dynamic-strength-workout/edit/circuit/' + item._id + "/" + athlete_id); 
  }
 
}


  return (
    <>
      <div
        ref={setNodeRef}
        {...attributes}
        {...(dragMode ? listeners : {})}  // Apply listeners when dragMode is true  // Apply listeners only when dragMode is true
        style={style}
        onMouseDown={() => setIsDragging(true)}  // Set dragging state on mouse down
        onMouseUp={() => setIsDragging(false)}   // Reset dragging state when mouse is up
      >
               {/* Item Type: Exercise */}
              {item.type === 'exercise' && (
                <div className="flex items-center p-2 bg-gray-50 border border-gray-200 rounded-lg shadow-sm mb-1">
                  <span className="flex-1 text-gray-800 font-semibold text-sm">
                    <span className="inline-block px-2 py-1 bg-white border border-gray-300 rounded-lg shadow-sm">
                      Exercise: {item.title}
                    </span>
                  </span>
                  <div className="flex space-x-2">
                    <button
                      className="px-2 py-1 text-blue-600 font-medium hover:text-blue-500 underline transition duration-150 ease-in-out"
                      onClick={handleEditClick}
                    >
                      Edit
                    </button>
                    <button
                      className="px-2 py-1 text-red-600 font-medium hover:text-red-500 underline transition duration-150 ease-in-out"
                      onClick={handleDeleteClick}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
                    
       {/* Item Type: Superset */}
        {item.type === 'superset' && (
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg shadow-sm mb-2">
            <div className="flex items-center justify-between">
              <span className="text-gray-800 font-semibold text-sm">
                <span className="inline-block px-2 py-1 bg-white border border-gray-300 rounded-lg shadow-sm">
                  Super Set
                </span>
              </span>
              <div className="flex space-x-2">
                <button
                  className="px-2 py-1 text-blue-600 font-medium hover:text-blue-500 underline transition duration-150 ease-in-out"
                  onClick={handleEditClick}
                >
                  Edit
                </button>
                <button
                  className="px-2 py-1 text-red-600 font-medium hover:text-red-500 underline transition duration-150 ease-in-out"
                  onClick={handleDeleteClick}
                >
                  Delete
                </button>
              </div>
            </div>
            <ul className="mt-2 ml-5 list-disc text-gray-600">
              {item.exercises.map((exercise, exIndex) => (
                <li key={exIndex} className="text-sm">
                  {exercise.title}
                </li>
              ))}
            </ul>
          </div>
        )}
        
       {/* Item Type: Circuit */}
  {item.type === 'circuit' && (
    <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg shadow-sm mb-2">
      <div className="flex items-center justify-between">
        <span className="text-gray-800 font-semibold text-sm">
          <span className="inline-block px-2 py-1 bg-white border border-gray-300 rounded-lg shadow-sm">
            Circuit
          </span>
        </span>
        <div className="flex space-x-2">
          <button
            className="px-2 py-1 text-blue-600 font-medium hover:text-blue-500 underline transition duration-150 ease-in-out"
            onClick={handleEditClick}
          >
            Edit
          </button>
          <button
            className="px-2 py-1 text-red-600 font-medium hover:text-red-500 underline transition duration-150 ease-in-out"
            onClick={handleDeleteClick}
          >
            Delete
          </button>
        </div>
      </div>
      <ul className="mt-2 ml-5 list-disc text-gray-600">
        {item.exercises.map((exercise, exIndex) => (
          <li key={exIndex} className="text-sm">
            {exercise.title}
          </li>
        ))}
      </ul>
    </div>
  )}
      </div>
    </>
  );
};

export default DraggableItem;




const Droppable = ({ id, children }) => {
  const { setNodeRef, isOver } = useDroppable({
    id,
  });

  return (
    <div
      ref={setNodeRef}
      className={`transition-all duration-150 ease-in-out ${
        isOver ? 'bg-gray-100' : ''
      } p-4 rounded-md`}
    >
      {children}
    </div>
  );
};
